import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FormSelectBox from '@views/components/Inputs/FormSelectBox';
import type { FormItemProps, SelectProps } from 'antd';
import i18n from 'i18next';

import { routeFileSelector } from '@/utilz';

import { useGetRateSchemaCargoTypeOptions } from '../hooks/use-get-rate-schema-cargo-type-options';

interface QuoteCargoTypesSelectProps
  extends Pick<FormItemProps, 'rules' | 'name'>,
    Pick<SelectProps, 'placeholder' | 'disabled' | 'allowClear'> {
  label?: string;
  editMode?: boolean;
  value?: unknown;
  tooltip?: string;
  margin?: string;
  mode?: 'multiple' | 'tags';
  showAll?: boolean;
  onChange?: (value?: unknown) => void;
  disableDefaultOptions?: boolean;
  singleLineMode?: boolean;
  isRequired?: boolean;
  filterOptionsBy?: string[];
}

export const QuoteCargoTypeSelect: FC<QuoteCargoTypesSelectProps> = ({
  placeholder = i18n.t('translation:default.fields.cargoType.placeholder'),
  label = i18n.t('translation:default.fields.cargoType.label'),
  rules = [],
  name,
  editMode,
  value,
  tooltip,
  margin = 'mb-0',
  mode,
  showAll = false,
  onChange,
  singleLineMode = false,
  disabled = false,
  allowClear,
  isRequired = false,
  filterOptionsBy,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const { data = [], isLoading } = useGetRateSchemaCargoTypeOptions();

  const options = useMemo(() => {
    if (!filterOptionsBy || filterOptionsBy.length === 0) {
      return data;
    }

    return data.filter((value) => filterOptionsBy.includes(value.value));
  }, [data, filterOptionsBy]);

  return (
    <FormSelectBox
      loading={isLoading}
      options={showAll ? [{ label: t('translation:default.options.all'), value: '-1' }, ...options] : options}
      placeholder={placeholder}
      label={label}
      margin={margin}
      rules={rules}
      name={name}
      editMode={editMode}
      value={value}
      tooltip={tooltip}
      mode={mode}
      onChange={onChange}
      singleLineMode={singleLineMode}
      disabled={disabled}
      allowClear={allowClear}
      isRequired={isRequired}
    />
  );
};
